import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Users, Car, Shield, Megaphone, Activity, Server, Settings, BarChart3, Command, CornerDownLeft } from 'lucide-react';
import axios from 'axios';

import api from '../utils/api';

const NAV_RESULTS = [
  { id: 'overview', label: 'Overview', desc: 'Platform stats & quick actions', icon: BarChart3, type: 'nav' },
  { id: 'users', label: 'Users', desc: 'Manage all platform users', icon: Users, type: 'nav' },
  { id: 'vehicles', label: 'Vehicles', desc: 'View all vehicle listings', icon: Car, type: 'nav' },
  { id: 'moderation', label: 'Moderation', desc: 'Wholesale & lead moderation', icon: Shield, type: 'nav' },
  { id: 'announcements', label: 'Announcements', desc: 'Platform announcements', icon: Megaphone, type: 'nav' },
  { id: 'activity', label: 'Activity Log', desc: 'Admin action history', icon: Activity, type: 'nav' },
  { id: 'health', label: 'System Health', desc: 'Server & database status', icon: Server, type: 'nav' },
  { id: 'settings', label: 'Settings', desc: 'Notification preferences', icon: Settings, type: 'nav' },
];

export default function CommandPalette({ open, onClose, onNavigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [userResults, setUserResults] = useState([]);
  const [vehicleResults, setVehicleResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      setUserResults([]);
      setVehicleResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const searchRemote = useCallback(async (q) => {
    if (q.length < 2) { setUserResults([]); setVehicleResults([]); return; }
    setSearching(true);
    try {
      const [usersRes, vehiclesRes] = await Promise.all([
        api.get('/api/admin/users', { params: { search: q, limit: 5 } }),
        api.get('/api/admin/vehicles', { params: { limit: 50 } })
      ]);
      setUserResults((usersRes.data.users || []).map(u => ({
        id: u.id, label: u.name, desc: `${u.email} · ${u.role}`, icon: Users, type: 'user', data: u
      })));
      const lq = q.toLowerCase();
      const matched = (vehiclesRes.data.vehicles || []).filter(v =>
        `${v.year} ${v.make} ${v.model} ${v.vin}`.toLowerCase().includes(lq)
      ).slice(0, 5);
      setVehicleResults(matched.map(v => ({
        id: v.id, label: `${v.year} ${v.make} ${v.model}`, desc: `${v.vin || 'No VIN'} · ${v.dealer_name}`, icon: Car, type: 'vehicle', data: v
      })));
    } catch {} finally { setSearching(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchRemote(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, searchRemote]);

  useEffect(() => {
    const lq = query.toLowerCase();
    const navMatches = !query ? NAV_RESULTS : NAV_RESULTS.filter(n => n.label.toLowerCase().includes(lq) || n.desc.toLowerCase().includes(lq));
    const all = [...navMatches, ...userResults, ...vehicleResults];
    setResults(all);
    setSelectedIdx(0);
  }, [query, userResults, vehicleResults]);

  const handleSelect = (item) => {
    onClose();
    if (item.type === 'nav') onNavigate(item.id);
    else if (item.type === 'user') onNavigate('users', { search: item.data.email });
    else if (item.type === 'vehicle') onNavigate('vehicles');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[selectedIdx]) { e.preventDefault(); handleSelect(results[selectedIdx]); }
    else if (e.key === 'Escape') onClose();
  };

  if (!open) return null;

  const grouped = { nav: [], user: [], vehicle: [] };
  results.forEach(r => grouped[r.type]?.push(r));
  const groupLabels = { nav: 'Pages', user: 'Users', vehicle: 'Vehicles' };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="command-palette">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed top-[15%] left-1/2 -translate-x-1/2 w-full max-w-xl">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-gray-100">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search users, vehicles, pages..."
              className="flex-1 py-4 text-base outline-none bg-transparent placeholder-gray-400"
              data-testid="command-palette-input"
            />
            <kbd className="hidden sm:flex items-center gap-1 text-[10px] font-medium text-gray-400 bg-gray-100 rounded-md px-1.5 py-0.5">ESC</kbd>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto py-2">
            {searching && <p className="text-center text-xs text-gray-400 py-3">Searching...</p>}
            {results.length === 0 && !searching && query.length > 1 && (
              <p className="text-center text-sm text-gray-400 py-8">No results for "{query}"</p>
            )}
            {['nav', 'user', 'vehicle'].map(type => grouped[type]?.length > 0 && (
              <div key={type}>
                <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{groupLabels[type]}</p>
                {grouped[type].map(item => {
                  const globalIdx = results.indexOf(item);
                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIdx(globalIdx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${globalIdx === selectedIdx ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      data-testid={`cmd-result-${item.type}-${item.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${globalIdx === selectedIdx ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                        <item.icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${globalIdx === selectedIdx ? 'text-blue-900' : 'text-gray-900'}`}>{item.label}</p>
                        <p className="text-xs text-gray-400 truncate">{item.desc}</p>
                      </div>
                      {globalIdx === selectedIdx && <CornerDownLeft className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ))}
            {!query && (
              <p className="text-center text-xs text-gray-400 py-4">Type to search across users, vehicles & pages</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center gap-4 px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
            <span className="flex items-center gap-1 text-[10px] text-gray-400"><kbd className="bg-gray-200 rounded px-1 py-0.5 font-mono">↑↓</kbd> navigate</span>
            <span className="flex items-center gap-1 text-[10px] text-gray-400"><kbd className="bg-gray-200 rounded px-1 py-0.5 font-mono">↵</kbd> select</span>
            <span className="flex items-center gap-1 text-[10px] text-gray-400"><kbd className="bg-gray-200 rounded px-1 py-0.5 font-mono">esc</kbd> close</span>
          </div>
        </div>
      </div>
    </div>
  );
}
